import {
  ArrowLeftSmIcon,
  ArrowRightSmIcon,
  Button,
  IconButton,
} from '@crono-one/design-system';
import { useAnalytics } from 'context/analytics';
import { cn } from 'utils/style-utils';
import { useSkipTaskWithConfirm } from 'hooks/services/task/usePatchTask';
import { useExecuteMultipleTasks } from './../startMultipleTasksContext';

interface TaskExecutionFooterActionsProps {
  taskId: number;
  className?: string;
  // Replaces the default single-task skip for the screens whose skip means
  // something else — InMail's "apply to all InMail tasks of today".
  onSkip?: () => void;
  isSkipping?: boolean;
  disabled?: boolean;
}

const TaskExecutionFooterActions = ({
  taskId,
  className,
  onSkip,
  isSkipping: isSkippingOverride,
  disabled,
}: TaskExecutionFooterActionsProps) => {
  const analytics = useAnalytics();

  const {
    goToPreviousTask,
    goToNextTask,
    onTaskHandled,
    canGoPrevious,
    canGoNext,
  } = useExecuteMultipleTasks();

  // Prev/Next only make sense when there is actually another task to step
  // through. A single task (opened on its own or a one-task flow) has neither,
  // so the navigation buttons are hidden.
  const canNavigateTasks = canGoPrevious || canGoNext;

  // After the skip succeeds drop the task from the flow and move on. The flow
  // advances itself, so the /task/search/next invalidation must not fire (it would
  // refetch the still-active skipped id.
  const { skipTaskWithConfirm, isPending: isDefaultSkipping } =
    useSkipTaskWithConfirm(
      () => {
        analytics?.track('patch-task', { type: 'skip' });
        onTaskHandled('skipped');
      },
      undefined,
      { invalidateNextTask: false },
    );

  const isSkipping = onSkip ? !!isSkippingOverride : isDefaultSkipping;

  return (
    <div className={cn('flex items-center gap-4 mr-16', className)}>
      <Button
        variant="outline"
        tone="gray"
        className="w-30"
        loading={isSkipping}
        disabled={disabled}
        onClick={() => (onSkip ? onSkip() : skipTaskWithConfirm(taskId))}
      >
        Skip
      </Button>
      {canNavigateTasks && (
        <div className="flex items-center gap-1">
          <IconButton
            variant="outline"
            tone="gray"
            shape="circle"
            icon={ArrowLeftSmIcon}
            label="Previous task"
            disabled={!canGoPrevious || isSkipping || disabled}
            onClick={goToPreviousTask}
          />
          <IconButton
            variant="outline"
            tone="gray"
            shape="circle"
            icon={ArrowRightSmIcon}
            label="Next task"
            disabled={!canGoNext || isSkipping || disabled}
            onClick={goToNextTask}
          />
        </div>
      )}
    </div>
  );
};

export default TaskExecutionFooterActions;
